import React from "react";
import ReactDOM from "react-dom";
import OrgSwitcher, {
  OrgSwitcherProps,
} from "@civicplus/preamble-ui/lib/OrgSwitcher/OrgSwitcher";
import AppSwitcher, {
  AppSwitcherProps,
} from "@civicplus/preamble-ui/lib/AppSwitcher";
import ProductThemeProvider from "@civicplus/preamble-ui/lib/ProductThemeProvider";
import IOrgService from "@civicplus/preamble-ui/lib/Services/OrgService/OrgService";
import OrgService from "@civicplus/preamble-ui/lib/Services/OrgService/OrgService";
import { CppOrganization } from "@civicplus/preamble-ui/lib/Services/OrgService/Types/Organization";

interface OrgAppSwitcherConfig
  extends React.ComponentProps<typeof ProductThemeProvider> {
  /**
   * The id of the DOM node to render the organization switcher in.
   * Defaults to `cp-orgSwitcher`
   */
  orgSwitcherNode?: string;
  /**
   * The id of the DOM node to render the application switcher in.
   * Defaults to `cp-appSwitcher`
   */
  appSwitcherNode?: string;
  /**
   * The credentials used to construct the OrgServiceSdk
   */
  orgService: IOrgService;
  /**
   * The current organization to pass to the pickers
   */
  currentOrg?: CppOrganization;
  /**
   * The organization to initialize the pickers too (if the currentOrg is not defined)
   */
  initialOrg?: string;
  /**
   * Any additional props to pass to the Organization Switcher
   */
  OrgSwitcherProps?: Partial<OrgSwitcherProps>;
  /**
   * Any additional props to pass to the Application Switcher
   */
  AppSwitcherProps?: Partial<AppSwitcherProps>;
}

export const init = async (config: OrgAppSwitcherConfig): Promise<void> => {
  const {
    orgSwitcherNode,
    appSwitcherNode,
    orgService,
    currentOrg,
    initialOrg,
    OrgSwitcherProps,
    AppSwitcherProps,
    ...themeProviderProps
  } = config;

  const orgServiceSdk = new OrgService(orgService);
  let renderedOrg = currentOrg;

  if (renderedOrg === undefined && initialOrg !== undefined) {
    renderedOrg = await orgServiceSdk.GetOrganization(initialOrg);
  }

  const orgSwitcherElement = document.getElementById(
    orgSwitcherNode || "cp-orgSwitcher"
  );
  if (orgSwitcherElement) {
    ReactDOM.render(
      <React.StrictMode>
        <ProductThemeProvider
          {...themeProviderProps}
          prefix={
            themeProviderProps.prefix
              ? themeProviderProps.prefix + "org"
              : "cp-orgSwitcher"
          }
        >
          <OrgSwitcher
            orgServiceSdk={orgServiceSdk}
            currentOrg={renderedOrg}
            {...OrgSwitcherProps}
          />
        </ProductThemeProvider>
      </React.StrictMode>,
      orgSwitcherElement
    );
  }

  const appSwitcherElement = document.getElementById(
    appSwitcherNode || "cp-appSwitcher"
  );
  if (appSwitcherElement && renderedOrg) {
    ReactDOM.render(
      <React.StrictMode>
        <ProductThemeProvider
          {...themeProviderProps}
          prefix={
            themeProviderProps.prefix
              ? themeProviderProps.prefix + "app"
              : "cp-appSwitcher"
          }
        >
          <AppSwitcher currentOrg={renderedOrg} {...AppSwitcherProps} />
        </ProductThemeProvider>
      </React.StrictMode>,
      appSwitcherElement
    );
  }
};

